import * as THREE from 'three';
import { lerp, lerpAngle, map } from '../maths';
import { latLongToVector3 } from '../earth';
import gsap from 'gsap';
import { ENV } from '../../config';

const LERP_FACTOR = ENV.PREFERS_REDUCED_MOTION ? 1 : 0.05;

export const CAMERA_MODE = {
    DEFAULT: 'DEFAULT',
    SPLASH: 'SPLASH',
    MODAL: 'MODAL',
    PANEL: 'PANEL'
}

export default {
    initCameras() {        
        // povGroup will allow us to get coordinates aligned with `this.camera`'s current orientation
        this.povGroup = new THREE.Group();
        this.povGroup.setRotationFromQuaternion(this.camera.quaternion);      

        // spherical coordinate helpers
        this.lerpedSpherical = new THREE.Spherical();
        this.controlsSpherical = new THREE.Spherical();

        this.controls.autoRotate = !ENV.PREFERS_REDUCED_MOTION;
        this.controls.enableDamping = !ENV.PREFERS_REDUCED_MOTION;
        this.controls.enablePan = false;
                       
        // Add camera clone which will lerp according to main camera
        this.lerpedCamera = this.newCamera();        
        this.lerpedCamera.position.copy(this.camera.position)
        this.lerpedCamera.lookAt(this.controls.target) 
        this.scene.add(this.lerpedCamera);

        // Add lerpCamera clone which will allow us to offset the point of view on local X/Y/Z
        this.offsetCamera = {
            instance: this.newCamera(),
            target: new THREE.Vector3(),
            min: new THREE.Vector3(),
            max: new THREE.Vector3()
        };                
        this.lerpedCamera.add(this.offsetCamera.instance);
        
        // Set the renderingCamera to be the offsetCamera
        this.renderingCamera = this.offsetCamera.instance;

        this.initCameraModes();
        this.setCameraMode(CAMERA_MODE.SPLASH, true);
        // requestAnimationFrame(() => {
        //     this.setCameraMode(CAMERA_MODE.SPLASH);
        // })

        if(false && this.DEBUG) {
            this.debugCamera = this.newCamera();
            this.debugCamera.position.set(10,10,10);
            this.debugCamera.lookAt(this.controls.target);

            const axesHelper = new THREE.AxesHelper();
            axesHelper.renderOrder = 999;
            axesHelper.material.depthTest = false;
            axesHelper.material.depthWrite = false;
            this.scene.add(axesHelper)

            const offsetHelper = new THREE.CameraHelper( this.offsetCamera.instance );
            this.scene.add( offsetHelper );

            const cameraHelper = new THREE.Mesh( new THREE.BoxGeometry(.05,.05,.05,1,1,1), new THREE.MeshBasicMaterial({ color: 0x00ff00 }));
            this.camera.add( cameraHelper );

            const lerpedHelper = new THREE.Mesh( new THREE.BoxGeometry(.1,.1,.1,1,1,1), new THREE.MeshBasicMaterial({ color: 0xff0000 }));
            this.lerpedCamera.add( lerpedHelper );

            this.renderingCamera = this.debugCamera;
        }
    },    

    goToCoordinates(coords, duration = ENV.PREFERS_REDUCED_MOTION ? 0 : 1) {
        // Setup target coord & main camera sphericals to help with calculations
        const coordSpherical = new THREE.Spherical().setFromVector3(latLongToVector3(coords.lat, coords.lng, 1));
        const cameraSpherical = new THREE.Spherical().setFromVector3(this.camera.position);
        
        // Take offsetCamera position into account and alter targetCoord spherical accordingly
        //// First, convert lerped & offsetSpherical to `povGroup` local coordinates to prevent desync near poles
        const localLerpedSpherical = new THREE.Spherical().setFromVector3(this.povGroup.worldToLocal(this.lerpedCamera.position.clone()))
        const localOffsetCameraSpherical = new THREE.Spherical().setFromVector3(this.povGroup.worldToLocal(this.lerpedCamera.localToWorld(this.offsetCamera.instance.position.clone())));
        //// Compute the spherical offset
        const offsetTheta = (localOffsetCameraSpherical.theta - localLerpedSpherical.theta);
        const offsetPhi = (localOffsetCameraSpherical.phi - localLerpedSpherical.phi);
        //// Remove that spherical offset from the coord target spherical
        coordSpherical.theta-=offsetTheta
        coordSpherical.phi-=offsetPhi

        // Compute theta's difference between main camera's spherical & target coord's spherical
        const thetaDifference = coordSpherical.theta - cameraSpherical.theta;
        if (Math.abs(thetaDifference) > Math.PI) { // If the difference is greater than a full revolution, there is necessarily a shorter way, let's get it here
            if (thetaDifference > 0) {
                // If the target is east of the start?
                coordSpherical.theta = coordSpherical.theta - (Math.PI*2);
            } else {
                // If the target is west of the start?
                coordSpherical.theta = coordSpherical.theta + (Math.PI*2);
            }
        }

        // Actually move the camera where wanted        
        if(duration == 0) { // If duration is null, for cut to it
            this.goToTw?.kill?.();
            gsap.set(cameraSpherical, { phi: coordSpherical.phi, theta: coordSpherical.theta }); // Move the position in the spherical system
            this.camera.position.setFromSpherical(cameraSpherical); // Update in the cartesian coordinates system
            this.lerpedCamera.position.setFromSpherical(cameraSpherical); // Force update the lerped
        } else { // If duration is greater than 0, let's tween it!
            this.goToTw?.kill?.();
            // Tween the spherical to orbit to the target
            this.goToTw = gsap.to(cameraSpherical, { phi: coordSpherical.phi, theta: coordSpherical.theta, duration, onUpdate: () => {
                this.camera.position.setFromSpherical(cameraSpherical); // Update in the cartesian coordinates system
            } })
        }
    },

    initCameraModes(){
        this.cameraModes = {};   

        this.cameraModes[CAMERA_MODE.DEFAULT] = {
            init: () => {
                this.controls.autoRotateSpeed = this.controls.autoRotateSpeedOriginal = .25;
                this.controls.enableZoom = true
                this.controls.minDistance = 2.5;
                this.controls.maxDistance = 5.2;                 
                this.setDistance(5.2);

                this.computeCameraMode();
                this.camera.position.y = 3;
            },
            screenPosition: () => { return { x: window.innerWidth/2, y: window.innerHeight / 2 }},
            onZoom: () => { 
                this.offsetCamera.target.y = map(this.controlsSpherical.radius, this.controls.minDistance, this.controls.maxDistance, .33, 0)
            }
        }
                
        this.cameraModes[CAMERA_MODE.PANEL] = {
            init: () => {
                this.controls.autoRotateSpeed = this.controls.autoRotateSpeedOriginal = .25;
                this.controls.enableZoom = true
                this.controls.minDistance = 2.5;
                this.controls.maxDistance = 5.2;      
                this.setDistance(5.2);

                this.computeCameraMode();
                this.camera.position.y = 3;
            },
            screenPosition: () => { 
                if(window.innerWidth < 700) {
                    return { x: window.innerWidth/2, y: window.innerHeight / 2 }
                } else {
                    const BCR = document.querySelector('[data-explore-ui="primary"]').getBoundingClientRect();

                    return { x: (window.innerWidth - BCR.width)/2, y: window.innerHeight / 2 } 
                }
            },
            onZoom: () => { 
                this.offsetCamera.target.y = map(this.controlsSpherical.radius, this.controls.minDistance, this.controls.maxDistance, .33, 0)
            }
        }

        this.cameraModes[CAMERA_MODE.SPLASH] = {
            init: () => {
                this.controls.autoRotateSpeed = this.controls.autoRotateSpeedOriginal = .25;
                this.controls.minDistance = 0;
                this.controls.maxDistance = 10; 
                this.setDistance(5);                
                this.controls.enableZoom = false                  

                this.computeCameraMode();
                this.camera.position.y = 0;              
            },
            screenPosition: () => { return { x: window.innerWidth/2, y: window.innerHeight } }
        }

        this.cameraModes[CAMERA_MODE.MODAL] = {
            init: () => {
                this.controls.autoRotateSpeed = this.controls.autoRotateSpeedOriginal = 0;
                this.controls.minDistance = 0;
                this.controls.maxDistance = 10;                           
                this.controls.enableZoom = false      
                
                if(window.innerWidth < 700) {
                    this.setDistance(6);
                } else {
                    this.setDistance(9);
                }

                this.computeCameraMode();
            },
            screenPosition: () => { 
                if(window.innerWidth < 700) {
                    return { x: window.innerWidth/2, y: window.innerHeight / 2 }
                } else {
                    const BCR = document.querySelector('#modal').parentNode.getBoundingClientRect();
                    return { x: (window.innerWidth - BCR.width)/2, y: window.innerHeight/2} 
                }
            }
        }
    },

    setCameraMode(value, instant) {
        if(value)
            this.cameraMode = value;    
        
        this.goToTw?.kill?.();
        
        this.cameraModes[this.cameraMode].init();
        if(instant) this.offsetCamera.instance.position.copy(this.offsetCamera.target.clone())    
    },
    setCameraModeInstant(value) { this.setCameraMode(value, true) },

    computeCameraMode() {
        const screenPosition = this.cameraModes[this.cameraMode].screenPosition();

        const target = this.computeOffsetCameraTargetFromScreenPosition(screenPosition)
        this.offsetCamera.target = this.offsetCamera.min = this.offsetCamera.max = target;        

        if(this.controls.enableZoom) {
            const min = this.computeOffsetCameraTargetFromScreenPosition(screenPosition, this.controls.minDistance)
            const max = this.computeOffsetCameraTargetFromScreenPosition(screenPosition, this.controls.maxDistance)

            this.offsetCamera.min = min;
            this.offsetCamera.max = max;
        }
    },        

    computeOffsetCameraTargetFromScreenPosition(desiredScreenPosition, distance = false) {
        const originalDistance = this.controls.getDistance()
        if(!distance) distance = originalDistance
        this.setDistance(distance)
        
        // Compute the desired coordinate in NDC (Normalized Device Coordinates)
        const ndcX = (desiredScreenPosition.x / this.sizes.width) * 2 - 1;
        const ndcY = -(desiredScreenPosition.y / this.sizes.height) * 2 + 1;

        // Force render
        this.renderer.render(this.scene, this.renderingCamera);
        
        // Create a raycaster from the camera's perspective to the desired viewport coordinate
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x: ndcX, y: ndcY }, this.camera);    
                
        // const dirHelper = new THREE.ArrowHelper( raycaster.ray.direction, raycaster.ray.origin, 9999, 0x0000ff);
        // this.scene.add(dirHelper);        

        const controlsNormal = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion).negate()

        const plane = new THREE.Plane(controlsNormal)
        // const planeHelper = new THREE.PlaneHelper( plane, 10, 0x00ff00 );
        // this.scene.add(planeHelper)
        
        // Compute the intersection point
        const intersectionPoint = new THREE.Vector3();
        raycaster.ray.intersectPlane(plane, intersectionPoint);

        // const intersectionHelper = new THREE.Mesh(new THREE.BoxGeometry(.1,.1,.1,1,1,1), new THREE.MeshBasicMaterial({ color: 0x00ffff }))
        // intersectionHelper.position.x = intersectionPoint.x;
        // intersectionHelper.position.y = intersectionPoint.y;
        // intersectionHelper.position.z = intersectionPoint.z;
        // this.scene.add(intersectionHelper);

        if(originalDistance != distance) {
            this.setDistance(originalDistance)
            this.renderer.render(this.scene, this.renderingCamera);
        }                    

        return this.povGroup.worldToLocal(intersectionPoint).negate();
    },        
    
    setDistance(value) {
        const spherical = new THREE.Spherical().setFromVector3(this.camera.position);
        spherical.radius = value;
        this.camera.position.setFromSpherical(spherical);
    },

    newCamera() {
        return new THREE.PerspectiveCamera(30, this.sizes.width / this.sizes.height, 0.01,100);
    },

    updateCameras() {
        // Update povGroup, allowing us to get coordinates aligned with `this.camera`'s current orientation
        this.povGroup.setRotationFromQuaternion(this.camera.quaternion); 

        // Refresh spherical coordinates
        this.lerpedSpherical.setFromVector3(this.lerpedCamera.position)
        this.controlsSpherical.setFromVector3(this.camera.position);

        // LERP CAMERA
        for(let axis of ['radius','phi','theta']) {
            let old = this.lerpedSpherical[axis]
            const target = this.controlsSpherical[axis]

            if(axis == 'theta') {
                this.lerpedSpherical[axis] = lerpAngle(old, target, LERP_FACTOR)
            } else {
                this.lerpedSpherical[axis] = lerp(old, target, LERP_FACTOR)
            }
        }
        this.lerpedCamera.position.setFromSpherical(this.lerpedSpherical);                
        this.lerpedCamera.lookAt(this.controls.target);

        // Update camera offset target according to current zoom
        if(this.controls.enableZoom) {
            this.offsetCamera.target.x = map(this.controlsSpherical.radius, this.controls.minDistance, this.controls.maxDistance, this.offsetCamera.min.x, this.offsetCamera.max.x)
            this.offsetCamera.target.y = map(this.controlsSpherical.radius, this.controls.minDistance, this.controls.maxDistance, this.offsetCamera.min.y, this.offsetCamera.max.y)
            this.offsetCamera.target.z = map(this.controlsSpherical.radius, this.controls.minDistance, this.controls.maxDistance, this.offsetCamera.min.z, this.offsetCamera.max.z)

            this.cameraModes[this.cameraMode].onZoom?.();
        }
        
        // Lerp camera offset local position
        for(let axis of ['x','y','z'])
            this.offsetCamera.instance.position[axis] = lerp(this.offsetCamera.instance.position[axis], this.offsetCamera.target[axis], LERP_FACTOR);
    },

    pauseAutoRotate() {
        this.controls.autoRotateSpeed = 0;
    },

    resumeAutoRotate() {
        this.controls.autoRotateSpeed = this.controls.autoRotateSpeedOriginal;
    }
}